import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Alert,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="h5"
      sx={{
        color: 'primary.main',
        fontWeight: 600,
        mb: 1.5,
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const SubSection = ({ title, children }) => (
  <Box sx={{ mt: 2.5, mb: 1.5 }}>
    <Typography
      variant="h6"
      sx={{ color: 'secondary.dark', fontWeight: 600, mb: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const BulletList = ({ items }) => (
  <List dense disablePadding sx={{ pl: 2 }}>
    {items.map((item, i) => (
      <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
        <ListItemText
          primary={item}
          primaryTypographyProps={{ variant: 'body2', sx: { lineHeight: 1.8 } }}
        />
      </ListItem>
    ))}
  </List>
);

const TermsOfService = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5, pb: 3, borderBottom: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}>
            Terms of Service
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Last Updated: January 13, 2026
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Effective for:</strong> Smart Industries Website & Mobile App
          </Typography>
        </Box>

        {/* Section 1 */}
        <Section number={1} title="Acceptance of Terms">
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By accessing and using <strong>Smart Industries</strong> (the "Service"), whether through our website
            at <Link href="https://smartindustriesllc.com" target="_blank" rel="noopener">smartindustriesllc.com</Link>{' '}
            or our mobile application, you accept and agree to be bound by these Terms of Service ("Terms").
          </Typography>
          <Typography variant="body1" paragraph>
            If you do not agree to these Terms, you must not use our Service.
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms constitute a legally binding agreement between you and <strong>Smart Industries LLC</strong>{' '}
            ("we," "us," or "our").
          </Typography>
        </Section>

        {/* Section 2 */}
        <Section number={2} title="Description of Service">
          <SubSection title="2.1 What We Provide">
            <Typography variant="body1" paragraph>Smart Industries is an informational service that:</Typography>
            <BulletList items={[
              'Checks if your email address, username, or phone number has been exposed in known data breaches',
              'Provides information about data breaches from public databases (Have I Been Pwned, XposedOrNot)',
              'Searches for class-action lawsuits related to breached companies',
              'Offers an identity theft recovery guide with steps for 30+ agencies',
              'Aggregates cybersecurity news from trusted sources',
            ]} />
          </SubSection>
          <SubSection title="2.2 What We Don't Provide">
            <BulletList items={[
              <><strong>Legal advice:</strong> Information is for reference only</>,
              <><strong>Dark web monitoring:</strong> We only check publicly known breaches</>,
              <><strong>Guaranteed breach detection:</strong> Results depend on third-party databases</>,
              <><strong>Credit monitoring:</strong> We do not monitor credit reports</>,
              <><strong>Identity theft insurance:</strong> We are not an insurance provider</>,
            ]} />
          </SubSection>
        </Section>

        {/* Section 3 */}
        <Section number={3} title="User Eligibility">
          <SubSection title="3.1 Age Requirement">
            <Typography variant="body1" paragraph>
              You must be at least <strong>13 years old</strong> to use this Service (or <strong>16 years old</strong>{' '}
              if you reside in the European Union).
            </Typography>
            <Typography variant="body1" paragraph>
              If you are under 18, you must have parental or guardian consent to use the Service.
            </Typography>
          </SubSection>
          <SubSection title="3.2 Account Responsibility">
            <Typography variant="body1" paragraph>
              While most features don't require an account, if you create one (future premium features):
            </Typography>
            <BulletList items={[
              'You must provide accurate information',
              'You are responsible for maintaining account security',
              'You must notify us immediately of unauthorized access',
              'You are responsible for all activities under your account',
            ]} />
          </SubSection>
        </Section>

        {/* Section 4 */}
        <Section number={4} title="Acceptable Use">
          <SubSection title="4.1 You May:">
            <BulletList items={[
              'Check your own personal information for breaches',
              'Use the identity recovery guide for legitimate recovery purposes',
              'Share breach check results with legal counsel',
              'Download and print the identity recovery checklist',
            ]} />
          </SubSection>
          <SubSection title="4.2 You May NOT:">
            <BulletList items={[
              <><strong>Check others' information without permission:</strong> Only check your own identifiers</>,
              <><strong>Abuse the service:</strong> No automated scraping, mass queries, or DDoS attacks</>,
              <><strong>Circumvent rate limits:</strong> Don't use VPNs or proxies to bypass rate limiting</>,
              <><strong>Reverse engineer:</strong> Don't attempt to copy, modify, or reverse engineer our Service</>,
              <><strong>Violate laws:</strong> Don't use the Service for illegal purposes</>,
              <><strong>Harass or threaten:</strong> Don't contact breached companies with threats or harassment</>,
            ]} />
          </SubSection>

          <Alert severity="warning" sx={{ my: 2 }}>
            <Typography variant="body2">
              <strong>Important: Checking Others' Information</strong>
              <br />
              Checking someone else's email or phone number without their explicit permission may violate privacy
              laws (GDPR, CCPA, Computer Fraud and Abuse Act).{' '}
              <strong>We strongly discourage this practice and are not liable for your misuse.</strong>
            </Typography>
          </Alert>
        </Section>

        {/* Section 5 */}
        <Section number={5} title="Advertising">
          <SubSection title="5.1 Display of Advertisements">
            <Typography variant="body1" paragraph>
              Our Service displays advertisements provided by <strong>Google AdSense</strong> (website) and{' '}
              <strong>Google AdMob</strong> (mobile app). By using our Service, you acknowledge that:
            </Typography>
            <BulletList items={[
              'Advertisements will be displayed on various pages of the Service',
              'We do not control the content of third-party advertisements',
              'Clicking on ads will take you to third-party websites not operated by us',
              'Google may use cookies and tracking technologies to serve relevant ads',
            ]} />
          </SubSection>
          <SubSection title="5.2 Ad Blockers">
            <Typography variant="body1" paragraph>
              While you may use ad-blocking software, we kindly ask that you consider disabling it for our Service.
              Advertising revenue helps us maintain free access to breach checking and security tools.
            </Typography>
          </SubSection>
          <SubSection title="5.3 Third-Party Ad Content">
            <Typography variant="body1" paragraph>
              We are not responsible for the content, accuracy, or opinions expressed in advertisements. Any
              transactions between you and advertisers are solely between you and the advertiser.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 6 */}
        <Section number={6} title="Intellectual Property">
          <SubSection title="6.1 Our Property">
            <Typography variant="body1" paragraph>
              All content, features, and functionality of Smart Industries, including but not limited to:
            </Typography>
            <BulletList items={[
              'Text, graphics, logos, icons',
              'Software code and APIs',
              'Identity recovery guide content',
              'Design and layout',
            ]} />
            <Typography variant="body1" paragraph>
              are owned by Smart Industries LLC and protected by copyright, trademark, and other intellectual property laws.
            </Typography>
          </SubSection>
          <SubSection title="6.2 Third-Party Content">
            <Typography variant="body1" paragraph>Breach data is sourced from third-party databases:</Typography>
            <BulletList items={[
              <><strong>Have I Been Pwned</strong> - Owned by Troy Hunt</>,
              <><strong>XposedOrNot</strong> - Third-party breach database</>,
              <><strong>ClassAction.org</strong> - Lawsuit information</>,
            ]} />
            <Typography variant="body1" paragraph>
              News articles are copyrighted by their respective publishers (TLDR, Krebs on Security, BleepingComputer).
              We provide summaries under fair use.
            </Typography>
          </SubSection>
          <SubSection title="6.3 User-Generated Content">
            <Typography variant="body1" paragraph>
              If you provide feedback, suggestions, or content to us, you grant us a perpetual, irrevocable,
              worldwide license to use, modify, and incorporate such content.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 7 */}
        <Section number={7} title="Privacy & Data Protection">
          <Typography variant="body1" paragraph>
            Your use of PII Breach Monitor is also governed by our{' '}
            <Link component={RouterLink} to="/privacy-policy">Privacy Policy</Link>.
          </Typography>
          <SubSection title="7.1 Data We Don't Store">
            <BulletList items={[
              <><strong>Search queries:</strong> Email/username/phone you check are NOT stored</>,
              <><strong>Breach results:</strong> NOT stored on our servers</>,
            ]} />
          </SubSection>
          <SubSection title="7.2 Data We Store (Optional)">
            <BulletList items={[
              <><strong>Identity Recovery progress:</strong> Encrypted on your device (mobile app)</>,
              <><strong>Email (optional):</strong> Only if you provide it for progress sync</>,
            ]} />
          </SubSection>
          <SubSection title="7.3 Your Rights">
            <Typography variant="body1" paragraph>You have the right to:</Typography>
            <BulletList items={[
              'Access your data',
              'Delete your data (use "Delete All Data" in mobile app Settings)',
              'Opt out of analytics',
            ]} />
            <Typography variant="body1" paragraph>
              See our <Link component={RouterLink} to="/privacy-policy">Privacy Policy</Link> for full details.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 8 */}
        <Section number={8} title="Disclaimers">
          <Alert severity="error" sx={{ my: 2 }}>
            <Typography variant="body2">
              <strong>No Legal or Financial Advice</strong>
              <br />
              Smart Industries is an informational service only. We do not provide: legal advice or representation,
              financial advice or credit counseling, identity theft resolution services, or guaranteed breach detection.
              <br /><br />
              <strong>Always consult with a qualified attorney, financial advisor, or credit counselor for personalized guidance.</strong>
            </Typography>
          </Alert>

          <SubSection title="8.1 Service Provided 'AS IS'">
            <Typography variant="body1" paragraph>
              The Service is provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis. We
              make no warranties or representations about:
            </Typography>
            <BulletList items={[
              <><strong>Accuracy:</strong> Breach data depends on third-party databases</>,
              <><strong>Completeness:</strong> Not all breaches may be detected</>,
              <><strong>Timeliness:</strong> Breach databases may not be up-to-date</>,
              <><strong>Availability:</strong> Service may be interrupted or unavailable</>,
            ]} />
          </SubSection>
          <SubSection title="8.2 Third-Party Accuracy">
            <Typography variant="body1" paragraph>
              We rely on third-party data sources (Have I Been Pwned, XposedOrNot, ClassAction.org).{' '}
              <strong>We are not responsible for the accuracy, completeness, or reliability of third-party information.</strong>
            </Typography>
          </SubSection>
          <SubSection title="8.3 No Guarantee of Recovery">
            <Typography variant="body1" paragraph>
              While our Identity Recovery guide provides steps to contact 30+ agencies,{' '}
              <strong>we cannot guarantee successful identity theft recovery or any specific outcome.</strong>
            </Typography>
          </SubSection>
        </Section>

        {/* Section 9 */}
        <Section number={9} title="Limitation of Liability">
          <Typography variant="body1" paragraph>To the maximum extent permitted by law:</Typography>
          <SubSection title="9.1 No Liability for Damages">
            <Typography variant="body1" paragraph>Smart Industries LLC shall not be liable for any:</Typography>
            <BulletList items={[
              'Indirect, incidental, special, or consequential damages',
              'Loss of profits, data, or business opportunities',
              'Damages resulting from unauthorized access to your information',
              'Damages from reliance on third-party breach data',
              'Damages from service interruptions or errors',
            ]} />
          </SubSection>
          <SubSection title="9.2 Maximum Liability Cap">
            <Typography variant="body1" paragraph>
              In no event shall our total liability exceed the amount you paid us in the past 12 months (if any).
              For free users, this is $0.
            </Typography>
          </SubSection>
          <SubSection title="9.3 Exceptions">
            <Typography variant="body1" paragraph>
              These limitations do not apply where prohibited by law or to damages caused by our gross negligence
              or willful misconduct.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 10 */}
        <Section number={10} title="Indemnification">
          <Typography variant="body1" paragraph>
            You agree to indemnify, defend, and hold harmless Smart Industries LLC, its officers, employees, and
            agents from any claims, damages, losses, or expenses (including attorneys' fees) arising from:
          </Typography>
          <BulletList items={[
            'Your violation of these Terms',
            'Your violation of any laws or third-party rights',
            "Your misuse of the Service (e.g., checking others' information without permission)",
            'Your reliance on information provided by the Service',
          ]} />
        </Section>

        {/* Section 11 */}
        <Section number={11} title="Termination">
          <SubSection title="11.1 Your Right to Terminate">
            <Typography variant="body1" paragraph>
              You may stop using the Service at any time. For mobile app, delete the app. For website, simply stop visiting.
            </Typography>
          </SubSection>
          <SubSection title="11.2 Our Right to Terminate">
            <Typography variant="body1" paragraph>We may suspend or terminate your access to the Service:</Typography>
            <BulletList items={[
              'If you violate these Terms',
              'If you abuse the service (excessive queries, automated scraping)',
              'If required by law',
              'At our sole discretion',
            ]} />
          </SubSection>
          <SubSection title="11.3 Effect of Termination">
            <Typography variant="body1" paragraph>Upon termination:</Typography>
            <BulletList items={[
              'Your right to use the Service ends immediately',
              'We may delete any data associated with your use (if applicable)',
              'Provisions that should survive (disclaimers, limitations of liability) will continue to apply',
            ]} />
          </SubSection>
        </Section>

        {/* Section 12 */}
        <Section number={12} title="Changes to Terms">
          <Typography variant="body1" paragraph>
            We reserve the right to modify these Terms at any time.
          </Typography>
          <SubSection title="12.1 How We Notify You">
            <Typography variant="body1" paragraph>
              We will post updated Terms on this page with a new "Last Updated" date. For material changes, we may also:
            </Typography>
            <BulletList items={[
              'Display an in-app notification (mobile app)',
              'Send an email (if you provided one)',
              'Show a prominent notice on the website',
            ]} />
          </SubSection>
          <SubSection title="12.2 Your Acceptance">
            <Typography variant="body1" paragraph>
              Continued use of the Service after changes constitutes acceptance of the modified Terms. If you don't
              agree to changes, you must stop using the Service.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 13 */}
        <Section number={13} title="Dispute Resolution">
          <SubSection title="13.1 Informal Resolution">
            <Typography variant="body1" paragraph>
              Before filing a legal claim, please contact us at{' '}
              <Link href="mailto:mikel.smart@smartindustriesllc.com">mikel.smart@smartindustriesllc.com</Link>{' '}
              to attempt informal resolution.
            </Typography>
          </SubSection>
          <SubSection title="13.2 Arbitration Agreement (US Users)">
            <Typography variant="body1" paragraph>
              Any dispute arising from these Terms or the Service shall be resolved by binding arbitration rather
              than in court, except:
            </Typography>
            <BulletList items={[
              'Small claims court disputes (under $10,000)',
              'Intellectual property disputes',
              'Where prohibited by law',
            ]} />
            <Typography variant="body1" paragraph>
              Arbitration shall be conducted by the American Arbitration Association (AAA) under its Consumer
              Arbitration Rules:
            </Typography>
            <BulletList items={[
              <><strong>Location:</strong> Florida, or by video conference at claimant's choice</>,
              <><strong>Fees:</strong> Filing fees shall be paid by the initiating party; each party pays their own attorneys' fees</>,
              <><strong>Decision:</strong> The arbitrator's decision is binding and may be entered in any court of competent jurisdiction</>,
            ]} />
          </SubSection>
          <SubSection title="13.3 Class Action Waiver">
            <Typography variant="body1" paragraph>
              You agree to bring claims only in your individual capacity, not as a class member or representative.
            </Typography>
          </SubSection>
          <SubSection title="13.4 Opt-Out">
            <Typography variant="body1" paragraph>
              You may opt out of arbitration within 30 days of first using the Service by emailing us at{' '}
              <Link href="mailto:mikel.smart@smartindustriesllc.com">mikel.smart@smartindustriesllc.com</Link>.
            </Typography>
          </SubSection>
        </Section>

        {/* Section 14 */}
        <Section number={14} title="Governing Law">
          <Typography variant="body1" paragraph>
            These Terms shall be governed by and construed in accordance with the laws of the{' '}
            <strong>State of Florida</strong>, United States, without regard to conflict of law principles.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>For EU Residents:</strong> The arbitration agreement in Section 13 does not apply to you. You
            retain the protection of the mandatory consumer protection laws of your country of residence, including
            the right to bring claims in your local court.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>For UK Residents:</strong> You retain all rights under UK consumer protection law, and disputes
            may be resolved in UK courts.
          </Typography>
        </Section>

        {/* Section 15 */}
        <Section number={15} title="Miscellaneous">
          <SubSection title="15.1 Entire Agreement">
            <Typography variant="body1" paragraph>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Smart Industries LLC.
            </Typography>
          </SubSection>
          <SubSection title="15.2 Severability">
            <Typography variant="body1" paragraph>
              If any provision is found unenforceable, the remaining provisions will remain in effect.
            </Typography>
          </SubSection>
          <SubSection title="15.3 No Waiver">
            <Typography variant="body1" paragraph>
              Our failure to enforce any right or provision does not constitute a waiver of that right.
            </Typography>
          </SubSection>
          <SubSection title="15.4 Assignment">
            <Typography variant="body1" paragraph>
              You may not assign these Terms. We may assign our rights and obligations to any party.
            </Typography>
          </SubSection>
          <SubSection title="15.5 Force Majeure">
            <Typography variant="body1" paragraph>
              We are not liable for delays or failures due to causes beyond our reasonable control (natural disasters,
              pandemics, cyberattacks, etc.).
            </Typography>
          </SubSection>
        </Section>

        {/* Section 16 */}
        <Section number={16} title="Third-Party Links and Services">
          <Typography variant="body1" paragraph>
            Our Service contains links to third-party websites and services not owned or controlled by Smart
            Industries LLC. We have no control over, and assume no responsibility for, the content, privacy policies,
            or practices of third-party sites.
          </Typography>
          <Typography variant="body1" paragraph>Third-party services we link to include but are not limited to:</Typography>
          <BulletList items={[
            <><strong>Have I Been Pwned</strong> (haveibeenpwned.com) - Breach database</>,
            <><strong>XposedOrNot</strong> - Breach database</>,
            <><strong>ClassAction.org</strong> - Lawsuit information</>,
            <><strong>Government agency websites</strong> - Linked in Identity Recovery guide</>,
            <><strong>Advertiser websites</strong> - Via Google AdSense/AdMob</>,
          ]} />
        </Section>

        {/* Section 17 */}
        <Section number={17} title="Service Modifications">
          <Typography variant="body1" paragraph>
            We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time
            with or without notice. We shall not be liable to you or any third party for any modification, suspension,
            or discontinuation of the Service.
          </Typography>
        </Section>

        {/* Section 18 */}
        <Section number={18} title="Accessibility">
          <Typography variant="body1" paragraph>
            Smart Industries LLC is committed to ensuring digital accessibility for people with disabilities. We
            continually improve the user experience and work to apply relevant accessibility standards (WCAG 2.1).
          </Typography>
          <Typography variant="body1" paragraph>
            If you experience any accessibility barriers while using our Service, please contact us at{' '}
            <Link href="mailto:mikel.smart@smartindustriesllc.com">mikel.smart@smartindustriesllc.com</Link>{' '}
            and we will work to address your concerns.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        {/* Contact Box */}
        <Paper
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ color: 'secondary.main', mb: 1 }}>
            Contact Us
          </Typography>
          <Typography variant="body1"><strong>Smart Industries LLC</strong></Typography>
          <Typography variant="body2">
            Email:{' '}
            <Link href="mailto:mikel.smart@smartindustriesllc.com" sx={{ color: 'secondary.main' }}>
              mikel.smart@smartindustriesllc.com
            </Link>
          </Typography>
          <Typography variant="body2">
            Website:{' '}
            <Link href="https://smartindustriesllc.com" sx={{ color: 'secondary.main' }}>
              smartindustriesllc.com
            </Link>
          </Typography>

          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

          <Typography variant="body2" sx={{ fontWeight: 600 }}>For Legal Inquiries:</Typography>
          <Typography variant="body2">
            Email:{' '}
            <Link href="mailto:mikel.smart@smartindustriesllc.com" sx={{ color: 'secondary.main' }}>
              mikel.smart@smartindustriesllc.com
            </Link>
          </Typography>
          <Typography variant="body2">Response time: Within 7-10 business days</Typography>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '2px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <strong>Virtus Pre Nummis</strong> - Valor Before Money
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            &copy; {new Date().getFullYear()} Smart Industries LLC. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsOfService;
